import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-gray-900">
            {/* Header */}
            <header className="bg-gray-800 py-4">
                <div className="container mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center gap-3">
                    <img src="/images/logo.png" alt="Fixisy Logo" className="h-8 w-8 sm:h-10 sm:w-10 object-contain" />
                    <Link to="/" className="text-xl sm:text-2xl font-bold text-cyan-400">Fixisy</Link>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Privacy Policy</h1>

                <p className="text-gray-300 text-sm sm:text-base mb-8">
                    Fixisy is a product offered and operated by <strong className="text-white">Kifayti Retail Private Limited</strong>.
                    Throughout this policy, &quot;Fixisy,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot; refers to Kifayti Retail Private Limited
                    in its capacity as the operator of the Fixisy platform.
                </p>

                <div className="prose prose-invert max-w-none text-sm sm:text-base">
                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
                        <p className="text-gray-300">
                            This Privacy Policy explains how Fixisy, a product of Kifayti Retail Private Limited, collects,
                            uses, and protects your personal information. We are committed to ensuring the privacy and security
                            of our users and their customers&apos; data. This policy applies to all platforms where our app is
                            available, including iOS, Android, and web platforms.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
                        <p className="text-gray-300">
                            2.1. Business Account Information:<br />
                            - Business name and contact details<br />
                            - Owner name and contact information<br />
                            - Business address and location<br />
                            - Payment information
                        </p>
                        <p className="text-gray-300 mt-4">
                            2.2. Customer Information:<br />
                            - Customer names and contact details<br />
                            - Repair service details<br />
                            - Payment records<br />
                            - Service history
                        </p>
                        <p className="text-gray-300 mt-4">
                            2.3. Device and Usage Information:<br />
                            - Device type and operating system<br />
                            - App usage statistics<br />
                            - IP address and location data<br />
                            - Performance and error data
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">3. Data Collection Permissions</h2>
                        <p className="text-gray-300">
                            3.1. Photo Library Access:<br />
                            We request access to your photo library to enable you to:<br />
                            - Upload photos of items requiring repair to document their condition<br />
                            - Take and store photos of repairs in progress to track repair status<br />
                            - Add business profile images to enhance your business listing<br />
                            - Share repair completion photos with customers<br />
                            You can manage this permission in your device settings at any time.
                        </p>
                        <p className="text-gray-300 mt-4">
                            3.2. Data Collection Purpose:<br />
                            We collect and use your data solely to provide app functionality:<br />
                            - Enable repair service management<br />
                            - Facilitate communication between businesses and customers<br />
                            - Process and record transactions<br />
                            - Maintain necessary business and repair records<br />
                            - Comply with legal and regulatory requirements<br />
                            We do not collect data for tracking or advertising purposes.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">4. How We Use Your Information</h2>
                        <p className="text-gray-300">
                            We use the collected information to:<br />
                            - Provide and improve our services<br />
                            - Process payments and transactions<br />
                            - Send service updates and notifications<br />
                            - Analyze usage patterns and improve user experience<br />
                            - Comply with legal obligations
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">5. Data Security</h2>
                        <p className="text-gray-300">
                            5.1. We implement appropriate technical and organizational measures to protect your data.<br />
                            5.2. Access to personal information is restricted to authorized personnel only.<br />
                            5.3. We regularly review and update our security practices.<br />
                            5.4. We use encryption for data transmission and storage.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">6. Data Sharing</h2>
                        <p className="text-gray-300">
                            We may share your information with:<br />
                            - Service providers who assist in our operations<br />
                            - Payment processors for transaction handling<br />
                            - Legal authorities when required by law<br />
                            We do not sell your personal information to third parties.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">7. Your Rights and Controls</h2>
                        <p className="text-gray-300">
                            7.1. Account Management:<br />
                            You have the right to:<br />
                            - Access and view your personal information<br />
                            - Correct or update inaccurate data<br />
                            - Export your data in a portable format<br />
                            - Delete your account through the app<br />
                            - Control app permissions and tracking preferences
                        </p>
                        <p className="text-gray-300 mt-4">
                            7.2. Data Export:<br />
                            You can request a copy of your data by:<br />
                            - Submitting a request through our support site at support.fixisy.com<br />
                            - Emailing your request to support@fixisy.com<br />
                            Your data will be provided within 30 days of the request in a commonly used format.<br />
                            The export will include:<br />
                            - Your personal information<br />
                            - Business records and repair history<br />
                            - Transaction data<br />
                            - Customer information associated with your account
                        </p>
                        <p className="text-gray-300 mt-4">
                            7.3. Account Deletion:<br />
                            When you request to delete your account:<br />
                            For Customer Accounts:<br />
                            1. Your account will be immediately disabled<br />
                            2. You will permanently lose access to the app<br />
                            3. Repair businesses will still be able to see your past repair history<br />
                            4. If you create a new account later, you won't have access to any previous repair records<br />
                            5. Your contact details and repair history will remain in the system for record-keeping purposes<br />
                            <br />
                            For Business Accounts:<br />
                            1. Your account will be immediately disabled<br />
                            2. You will permanently lose access to the app<br />
                            3. Customers can still see their past repair records with your business<br />
                            4. If you create a new account later, you won't have access to any previous business records or customer history<br />
                            5. Your business details and repair records will remain in the system for record-keeping purposes<br />
                            <br />
                            To initiate account deletion:<br />
                            1. Go to Profile in the app<br />
                            2. Select "Delete Account"<br />
                            3. Review the warning message<br />
                            4. Confirm your decision<br />
                            You can request a copy of your data before deletion by contacting support.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">8. Children's Privacy</h2>
                        <p className="text-gray-300">
                            Our services are not intended for children under 13. We do not knowingly collect or
                            maintain information from children under 13 years of age.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">9. Changes to Privacy Policy</h2>
                        <p className="text-gray-300">
                            We may update this Privacy Policy periodically. We will notify you of significant changes
                            through the App or via email. Continued use of our services after changes indicates your
                            acceptance of the updated policy.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">10. Grievances and Jurisdiction</h2>
                        <p className="text-gray-300">
                            If you have a privacy-related complaint or grievance regarding Fixisy, please contact us using
                            the details in Section 11. We will endeavour to resolve your concern in a timely manner. Any
                            dispute or complaint that cannot be resolved informally shall be subject to the exclusive
                            jurisdiction of the courts at <strong className="text-white">Giridih, Jharkhand</strong>, India,
                            and shall be governed by applicable Indian law.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">11. Contact Us</h2>
                        <p className="text-gray-300">
                            If you have questions about this Privacy Policy or your personal data, please contact
                            Kifayti Retail Private Limited (Fixisy) at:<br />
                            Email: privacy@fixisy.com<br />
                            For general support: support@fixisy.com
                        </p>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default PrivacyPolicy; 